import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAmazonProfile } from './AmazonProfileContext';
import { getPeriodLabel, resolveDateRangeBounds } from '../utils/dateRangeUtils';

const STORAGE_PREFIX = 'metricsDateRange';

const MetricsDateRangeContext = createContext(null);

function loadStored(profileId) {
  if (!profileId) return null;
  try {
    const key = `${STORAGE_PREFIX}_${profileId}`;
    let raw = localStorage.getItem(key);
    if (!raw) {
      raw = sessionStorage.getItem(key);
      if (raw) localStorage.setItem(key, raw);
    }
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStored(profileId, dateRange, customDateRange) {
  if (!profileId) return;
  localStorage.setItem(
    `${STORAGE_PREFIX}_${profileId}`,
    JSON.stringify({ dateRange, customDateRange })
  );
}

export function MetricsDateRangeProvider({ children }) {
  const { selectedProfile } = useAmazonProfile();
  const profileId = selectedProfile?.profileId;

  const [dateRange, setDateRange] = useState('30d');
  const [customDateRange, setCustomDateRange] = useState(null);

  useEffect(() => {
    const stored = loadStored(profileId);
    if (stored) {
      setDateRange(stored.dateRange || '30d');
      setCustomDateRange(stored.customDateRange || null);
    } else {
      setDateRange('30d');
      setCustomDateRange(null);
    }
  }, [profileId]);

  useEffect(() => {
    if (profileId) {
      saveStored(profileId, dateRange, customDateRange);
    }
  }, [profileId, dateRange, customDateRange]);

  const handleDateRangeChange = useCallback((newDateRange) => {
    if (typeof newDateRange === 'object' && newDateRange.type === 'custom') {
      setCustomDateRange(newDateRange);
      setDateRange('custom');
    } else {
      setDateRange(newDateRange);
      setCustomDateRange(null);
    }
  }, []);

  const activeDateBounds = useMemo(
    () => resolveDateRangeBounds(dateRange, customDateRange),
    [dateRange, customDateRange]
  );

  const periodLabel = getPeriodLabel(customDateRange || dateRange);
  const dateRangeKey = `${activeDateBounds.startDate}_${activeDateBounds.endDate}`;

  const value = useMemo(() => ({
    dateRange,
    customDateRange,
    setDateRange,
    setCustomDateRange,
    handleDateRangeChange,
    activeDateBounds,
    periodLabel,
    dateRangeKey
  }), [
    dateRange,
    customDateRange,
    handleDateRangeChange,
    activeDateBounds,
    periodLabel,
    dateRangeKey
  ]);

  return (
    <MetricsDateRangeContext.Provider value={value}>
      {children}
    </MetricsDateRangeContext.Provider>
  );
}

export function useMetricsDateRange() {
  const context = useContext(MetricsDateRangeContext);
  if (!context) {
    throw new Error('useMetricsDateRange must be used within MetricsDateRangeProvider');
  }
  return context;
}
